import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flag, Loader2, ShieldCheck, FileCheck2, User2, Clock, XCircle, MapPin, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg, fileUrl, uploadFile } from "../lib/api";
import { useI18n } from "../i18n";

export const LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_e96282d4-5752-460f-ba8d-c55fde0a5025/artifacts/58920fb217b37189_PrideFocus%20Lion%20Community%20Logo.png";

export function useMeta(title, { noindex = true, description } = {}) {
  useEffect(() => {
    document.title = title ? `${title} · PrideFocus.world` : "PrideFocus.world · Be Proud of What You Can Do.";
    const set = (name, content, attr = "name") => {
      let m = document.querySelector(`meta[${attr}="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    set("robots", noindex ? "noindex, nofollow" : "index, follow");
    if (description) { set("description", description); set("og:description", description, "property"); }
    set("og:title", document.title, "property");
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.rel = "canonical"; document.head.appendChild(c); }
    c.href = window.location.origin + window.location.pathname;
  }, [title, noindex, description]);
}

export function Avatar({ src, name, size = 48, className = "" }) {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return src ? (
    <img src={fileUrl(src)} alt={name || "Profile photo"} loading="lazy" style={{ width: size, height: size }} className={`rounded-full object-cover bg-slate-100 ${className}`} />
  ) : (
    <div style={{ width: size, height: size, fontSize: size / 2.8 }} aria-hidden className={`rounded-full grid place-items-center bg-slate-900 text-orange-400 font-display font-bold ${className}`}>{initials}</div>
  );
}

const TRUST = {
  self_declared: ["bg-slate-100 text-slate-600", User2], evidence_uploaded: ["bg-amber-50 text-amber-700", FileCheck2],
  pending_review: ["bg-blue-50 text-blue-700", Clock], verified: ["bg-emerald-50 text-emerald-700", ShieldCheck],
  rejected: ["bg-red-50 text-red-600", XCircle], employer_verified: ["bg-emerald-50 text-emerald-700", BadgeCheck], unverified: ["bg-slate-100 text-slate-600", User2],
};
export function TrustBadge({ status }) {
  const { t } = useI18n();
  const [cls, Icon] = TRUST[status] || TRUST.self_declared;
  return <span data-testid={`trust-${status}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}><Icon size={12} />{t(`trust.${status}`)}</span>;
}

export const DemoBadge = () => { const { t } = useI18n(); return <span className="whitespace-nowrap rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{t("demo")}</span>; };

export const Spinner = ({ label }) => <div className="flex items-center justify-center gap-2 py-12 muted" data-testid="loading"><Loader2 className="animate-spin" size={18} />{label || "Loading…"}</div>;

export function Empty({ icon: Icon, title, body, action }) {
  return (
    <div className="card p-10 text-center" data-testid="empty-state">
      {Icon && <Icon className="mx-auto text-orange-500 mb-3" size={28} />}
      <h3 className="font-semibold text-lg">{title}</h3>
      {body && <p className="muted text-sm mt-1 max-w-md mx-auto">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ProfileCard({ p }) {
  const { t } = useI18n();
  return (
    <Link to={`/u/${p.username}`} className="card-hover p-5 flex flex-col gap-3" data-testid={`profile-card-${p.username}`}>
      <div className="flex items-center gap-3">
        <Avatar src={p.photo_url} name={p.display_name} size={52} />
        <div className="min-w-0">
          <div className="font-semibold truncate flex items-center gap-2">{p.display_name}</div>
          <div className="text-xs muted line-clamp-2">{p.headline}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">{(p.skills || []).slice(0, 4).map((s) => <span key={s} className="chip">{s}</span>)}</div>
      <div className="flex items-center justify-between text-xs muted mt-auto">
        <span className="flex items-center gap-1">{p.city && <><MapPin size={12} />{p.city}{p.country ? `, ${p.country}` : ""}</>}</span>
        <span className="flex gap-1.5 items-center">{p.is_demo && <DemoBadge />}{p.availability && p.availability !== "not_available" && <span className="text-emerald-700 font-medium">{t(`av.${p.availability}`)}</span>}</span>
      </div>
      {(p.project_count > 0 || p.verified_credentials > 0) && <div className="flex gap-1.5">{p.project_count > 0 && <TrustBadge status="evidence_uploaded" />}{p.verified_credentials > 0 && <TrustBadge status="verified" />}</div>}
    </Link>
  );
}

export function EntityCard({ e, base }) {
  return (
    <Link to={`${base}${e.slug}`} className="card-hover p-5 flex flex-col gap-2" data-testid={`entity-card-${e.slug}`}>
      <div className="flex items-center gap-3">
        {e.logo_url ? <img src={fileUrl(e.logo_url)} alt="" className="w-11 h-11 rounded-xl object-cover" /> :
          <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-700 grid place-items-center font-display font-bold">{e.name[0]}</div>}
        <div className="min-w-0"><div className="font-semibold truncate">{e.name}</div>
          <div className="text-xs muted">{e.member_count || 0} members · {e.visibility === "private" ? "Private" : "Public"}{e.kind === "platform" ? " · Platform-managed" : ""}</div></div>
      </div>
      <p className="text-sm text-slate-600 line-clamp-2">{e.description}</p>
      <div className="flex flex-wrap gap-1.5 mt-auto">{[...(e.skills || []), ...(e.tags || [])].slice(0, 4).map((s) => <span key={s} className="chip">{s}</span>)}{e.is_demo && <DemoBadge />}</div>
      {e.my_status && <span className="text-xs font-semibold text-orange-600">Your status: {e.my_status}</span>}
    </Link>
  );
}

const REASONS = ["spam", "harassment", "impersonation", "plagiarism", "fake_credentials", "scam", "misleading", "inappropriate", "other"];
export function ReportButton({ type, id, small = true }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const { t } = useI18n();
  const submit = async () => {
    try { await api.post("/reports", { target_type: type, target_id: id, reason, details }); toast.success("Report sent to moderators. Thank you."); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className={small ? "text-xs muted hover:text-red-600 inline-flex items-center gap-1" : "btn-ghost btn-sm"} data-testid={`report-${type}-${id}`}><Flag size={12} />{t("p.report")}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>Report {type}</DialogTitle></DialogHeader>
          <label className="lbl" htmlFor="report-reason">Reason</label>
          <select id="report-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="report-reason-select">
            {REASONS.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
          </select>
          <textarea className="input mt-3" rows={3} placeholder="Details (optional)" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details-input" />
          <button className="btn-accent mt-3" onClick={submit} data-testid="report-submit-btn">Submit report</button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function TagInput({ value = [], onChange, suggestions = [], placeholder, testid }) {
  const [v, setV] = useState("");
  const add = (x) => { const s = x.trim(); if (s && !value.includes(s)) onChange([...value, s]); setV(""); };
  const sug = v ? suggestions.filter((s) => s.toLowerCase().includes(v.toLowerCase()) && !value.includes(s)).slice(0, 6) : [];
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">{value.map((x) => (
        <button type="button" key={x} className="chip-on" onClick={() => onChange(value.filter((y) => y !== x))} aria-label={`Remove ${x}`}>{x} ×</button>))}</div>
      <input className="input" value={v} placeholder={placeholder} data-testid={testid} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(v); } }} />
      {sug.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{sug.map((s) => <button type="button" key={s} className="chip hover:bg-orange-100" onClick={() => add(s)}>+ {s}</button>)}</div>}
    </div>
  );
}

export function UploadButton({ purpose, onDone, label = "Upload", accept = "image/*", testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { onDone(await uploadFile(f, purpose)); toast.success("Uploaded"); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false); e.target.value = "";
  };
  return (
    <label className="btn-ghost btn-sm cursor-pointer" data-testid={testid}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : null}{label}
      <input type="file" accept={accept} className="sr-only" onChange={pick} />
    </label>
  );
}
